import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, History, Package, Pencil, Plus, Trash2, Sparkles, Languages } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { apiError } from "@/lib/api";
import { money, fmtDate, langName } from "@/lib/constants";
import { useLang } from "@/i18n";
import { useStats } from "@/context/StatsContext";
import { Panel, Empty, RowSearch, EmptyInline } from "@/pages/shared";
import OrderAdvice from "@/pages/admin/OrderAdvice";

const UNITS = ["stuk", "liter", "kg", "meter", "pak", "uur", "dienst"];
const ART_LANGS = [["fr", "Français"], ["de", "Deutsch"], ["no", "Norsk"], ["es", "Español"], ["en", "English"]];
const blankArticle = () => ({ code: "", name: "", names: {}, category: "", unit: "stuk", price: "", vat_rate: 21, min_stock: 0, order_qty: 0, supplier_id: "", track_stock: true, active: true });
const KIND_KEY = { sale: "move_sale", delivery: "move_delivery", correction: "move_correction" };

export default function AdminStock() {
  const { t, lang } = useLang();
  const { reload: reloadStats } = useStats();
  const [articles, setArticles] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [tick, setTick] = useState(0);
  const [q, setQ] = useState("");
  const [dlg, setDlg] = useState(null); // article | moves
  const [form, setForm] = useState(blankArticle());
  const [moves, setMoves] = useState([]);
  const [current, setCurrent] = useState(null);
  const [correction, setCorrection] = useState({ qty: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);

  // Vertaalt de artikelnaam met AI (merknamen en maten blijven ongewijzigd)
  const aiTranslate = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post("/translate", {
        text: form.name, source: "nl", targets: ART_LANGS.map(([c]) => c),
        context: "naam van een winkelartikel op een camping. Laat merknamen, volumes, maten en verpakkingen altijd ongewijzigd (bv. 'Coca-Cola 33cl' blijft identiek) en vertaal enkel het beschrijvende deel",
      });
      setForm((f) => ({ ...f, names: { ...f.names, ...data } }));
      toast.success(t("ai_translated"));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setAiBusy(false); }
  };

  const translateAll = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post("/articles/translate-missing");
      toast.success(`${t("ai_translated")}: ${data.updated}/${data.total}`);
      await load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setAiBusy(false); }
  };

  const load = useCallback(async () => {
    try {
      const [arts, sups] = await Promise.all([api.get("/articles"), api.get("/suppliers")]);
      setArticles(arts.data); setSuppliers(sups.data); setTick((n) => n + 1); reloadStats();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, [reloadStats]);
  useEffect(() => { load(); }, [load]);

  const visible = useMemo(() => {
    const s = q.trim().toLowerCase();
    return articles.filter((a) => !s || [a.code, a.name, a.category].some((v) => (v || "").toLowerCase().includes(s)));
  }, [articles, q]);
  const low = articles.filter((a) => a.track_stock && (a.min_stock || 0) > 0 && a.stock <= a.min_stock).length;

  const price = Number(form.price) || 0;
  const vat = Number(form.vat_rate) || 0;
  const incl = price * (1 + vat / 100);

  const saveArticle = async () => {
    setBusy(true);
    const body = { ...form, price, vat_rate: vat, min_stock: Number(form.min_stock) || 0, order_qty: Number(form.order_qty) || 0, supplier_id: form.supplier_id || null };
    try {
      if (current?.id) await api.put(`/articles/${current.id}`, body);
      else await api.post("/articles", body);
      setDlg(null); await load(); toast.success(t("saved"));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const removeArticle = async (a) => {
    try { await api.delete(`/articles/${a.id}`); await load(); toast.success(t("deleted")); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const openMoves = async (a) => {
    setCurrent(a); setDlg("moves");
    try { setMoves((await api.get(`/articles/${a.id}/moves`)).data); } catch (_) { setMoves([]); }
  };

  const saveCorrection = async () => {
    setBusy(true);
    try {
      await api.post("/stock/correction", { article_id: current.id, qty: Number(correction.qty), reason: correction.reason });
      setDlg(null); setCorrection({ qty: "", reason: "" }); await load(); toast.success(t("saved"));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-5">
    <Panel
      title={`${t("articles")} (${visible.length}${q ? `/${articles.length}` : ""})`}
      testid="articles-panel"
      action={
        <div className="flex items-center gap-2">
          <RowSearch value={q} onChange={setQ} placeholder={t("search_article")} testid="articles-search" />
          <Button variant="outline" onClick={translateAll} disabled={aiBusy} data-testid="translate-all-articles-button">
            <Languages className={`h-4 w-4 mr-1 ${aiBusy ? "animate-pulse" : ""}`} />{t("art_translate_all")}
          </Button>
          <Button onClick={() => { setCurrent(null); setForm(blankArticle()); setDlg("article"); }} data-testid="new-article-button"><Plus className="h-4 w-4 mr-1" />{t("new_article")}</Button>
        </div>
      }
    >
      <div className="px-5 pt-4 flex flex-wrap items-center gap-4 text-sm">
        <span className="text-muted-foreground">{t("stock_desc")}</span>
        {low > 0 && <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 text-amber-700 px-2.5 py-1 text-xs font-semibold" data-testid="low-stock-badge"><AlertTriangle className="h-3.5 w-3.5" />{t("low_stock")}: {low}</span>}
      </div>
      {visible.length === 0 ? <Empty /> : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("art_code")}</TableHead>
              <TableHead>{t("art_name")}</TableHead>
              <TableHead>{t("art_category")}</TableHead>
              <TableHead className="text-right">{t("unit_price")}</TableHead>
              <TableHead className="text-right">{t("vat")}</TableHead>
              <TableHead className="text-right">{t("price_incl_vat")}</TableHead>
              <TableHead className="text-right">{t("art_stock")}</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.map((a) => {
              const lowRow = a.track_stock && (a.min_stock || 0) > 0 && a.stock <= a.min_stock;
              return (
                <TableRow key={a.id} data-testid={`article-row-${a.code}`} className={a.active ? "" : "opacity-50"}>
                  <TableCell className="font-mono text-xs font-bold">{a.code}</TableCell>
                  <TableCell className="font-semibold text-foreground">{a.name}<span className="ml-2 text-[11px] text-muted-foreground">{t(`unit_${a.unit}`)}</span></TableCell>
                  <TableCell className="text-muted-foreground">{a.category || "—"}</TableCell>
                  <TableCell className="text-right tabular-nums">{money(a.price)}</TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">{a.vat_rate}%</TableCell>
                  <TableCell className="text-right tabular-nums font-semibold" data-testid={`article-incl-${a.code}`}>{money(a.price * (1 + (a.vat_rate || 0) / 100))}</TableCell>
                  <TableCell className="text-right">
                    {a.track_stock ? (
                      <span className={`inline-flex items-center gap-1 font-bold tabular-nums ${a.stock < 0 ? "text-rose-600" : lowRow ? "text-amber-600" : "text-foreground"}`} data-testid={`article-stock-${a.code}`}>
                        {lowRow && <AlertTriangle className="h-3.5 w-3.5" />}{a.stock}
                      </span>
                    ) : <span className="text-xs text-muted-foreground">{t("no_stock_tracking")}</span>}
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button variant="ghost" size="sm" onClick={() => openMoves(a)} title={t("stock_history")} data-testid={`moves-${a.code}`}><History className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => { setCurrent(a); setForm({ ...blankArticle(), ...a }); setDlg("article"); }} data-testid={`edit-article-${a.code}`}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => removeArticle(a)} data-testid={`delete-article-${a.code}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      {/* Artikel aanmaken/wijzigen */}
      <Dialog open={dlg === "article"} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent data-testid="article-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Package className="h-4 w-4" />{current ? current.name : t("new_article")}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label className="text-xs">{t("art_code")}</Label><Input required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} data-testid="article-code" /></div>
            <div className="space-y-1"><Label className="text-xs">{t("art_category")}</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="article-category" /></div>
            <div className="space-y-1 col-span-2"><Label className="text-xs">{t("art_name")}</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="article-name" /></div>
            <div className="space-y-1">
              <Label className="text-xs">{t("art_unit")}</Label>
              <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                <SelectTrigger data-testid="article-unit"><SelectValue /></SelectTrigger>
                <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{t(`unit_${u}`)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label className="text-xs">{t("unit_price")} ({t("excl_vat")})</Label><Input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="article-price" /></div>
            <div className="space-y-1"><Label className="text-xs">{t("vat")} %</Label><Input type="number" step="1" value={form.vat_rate} onChange={(e) => setForm({ ...form, vat_rate: e.target.value })} data-testid="article-vat" /></div>
            <div className="space-y-1"><Label className="text-xs">{t("art_min_stock")}</Label><Input type="number" step="1" value={form.min_stock} onChange={(e) => setForm({ ...form, min_stock: e.target.value })} data-testid="article-min-stock" /></div>
            <div className="space-y-1"><Label className="text-xs">{t("art_order_qty")}</Label><Input type="number" step="1" min="0" value={form.order_qty ?? 0} onChange={(e) => setForm({ ...form, order_qty: e.target.value })} data-testid="article-order-qty" /></div>
            <div className="space-y-1">
              <Label className="text-xs">{t("art_supplier")}</Label>
              <Select value={form.supplier_id || "none"} onValueChange={(v) => setForm({ ...form, supplier_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="article-supplier"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t("no_supplier")}</SelectItem>
                  {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <p className="col-span-2 text-[11px] text-muted-foreground">{t("art_order_qty_hint")}</p>
            <div className="col-span-2 rounded-lg bg-muted/60 px-3 py-2.5 text-sm space-y-1" data-testid="article-calc">
              <div className="flex items-center justify-between text-muted-foreground"><span>{t("unit_price")} ({t("excl_vat")})</span><span className="tabular-nums">{money(price)}</span></div>
              <div className="flex items-center justify-between text-muted-foreground"><span>{t("vat")} {vat}%</span><span className="tabular-nums">{money(incl - price)}</span></div>
              <div className="flex items-center justify-between border-t border-border pt-1"><span className="font-semibold text-foreground">{t("price_incl_vat")}</span><span className="font-display font-extrabold text-primary tabular-nums" data-testid="article-total-incl">{money(incl)}</span></div>
            </div>
            <label className="flex items-center gap-2 text-sm col-span-2"><input type="checkbox" checked={form.track_stock} onChange={(e) => setForm({ ...form, track_stock: e.target.checked })} data-testid="article-track" />{t("art_track_stock")}</label>
            <label className="flex items-center gap-2 text-sm col-span-2"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="article-active" />{t("active")}</label>
            <div className="col-span-2 space-y-2 rounded-lg border border-border bg-muted/30 p-3" data-testid="article-names">
              <div className="flex items-center justify-between gap-2">
                <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{t("art_names")}</p>
                <Button type="button" variant="outline" size="sm" className="h-7 rounded-full" onClick={aiTranslate}
                  disabled={aiBusy || !form.name.trim()} data-testid="article-ai-translate-button">
                  <Sparkles className={`h-3.5 w-3.5 mr-1 ${aiBusy ? "animate-pulse" : ""}`} />{aiBusy ? t("ai_translating") : t("ai_translate")}
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {ART_LANGS.map(([code]) => (
                  <div key={code} className="space-y-1">
                    <Label className="text-[11px] text-muted-foreground">{langName(code, lang)}</Label>
                    <Input className="h-8" value={form.names?.[code] || ""} data-testid={`article-name-${code}`}
                      onChange={(e) => setForm({ ...form, names: { ...form.names, [code]: e.target.value } })} />
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground">{t("art_names_hint")}</p>
            </div>
            <p className="col-span-2 text-[11px] text-muted-foreground">{t("stock_rule_hint")}</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(null)}>{t("cancel")}</Button>
            <Button onClick={saveArticle} disabled={busy || !form.code || !form.name} data-testid="save-article-button">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Voorraadhistoriek + correctie */}
      <Dialog open={dlg === "moves"} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-w-2xl" data-testid="moves-dialog">
          <DialogHeader><DialogTitle className="font-display">{t("stock_history")} — {current?.name}</DialogTitle></DialogHeader>
          <div className="max-h-80 overflow-auto cb-scroll">
            {moves.length === 0 ? <EmptyInline /> : (
              <Table>
                <TableHeader><TableRow><TableHead>{t("date")}</TableHead><TableHead>{t("move_kind")}</TableHead><TableHead className="text-right">{t("qty")}</TableHead><TableHead>{t("note")}</TableHead><TableHead>{t("logged_in_as")}</TableHead></TableRow></TableHeader>
                <TableBody>
                  {moves.map((m) => (
                    <TableRow key={m.id} data-testid={`move-row-${m.id}`}>
                      <TableCell className="text-muted-foreground text-xs">{fmtDate(m.created_at)}</TableCell>
                      <TableCell className="text-xs font-semibold">{t(KIND_KEY[m.kind] || m.kind)}</TableCell>
                      <TableCell className={`text-right font-bold tabular-nums ${m.qty < 0 ? "text-rose-600" : "text-emerald-600"}`}>{m.qty > 0 ? `+${m.qty}` : m.qty}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{m.note}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{m.user}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
          <div className="border-t border-border pt-3 space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("new_correction")}</p>
            <div className="flex items-end gap-2">
              <div className="space-y-1 w-28"><Label className="text-xs">{t("qty")}</Label><Input required type="number" step="1" value={correction.qty} onChange={(e) => setCorrection({ ...correction, qty: e.target.value })} data-testid="correction-qty" /></div>
              <div className="space-y-1 flex-1"><Label className="text-xs">{t("correction_reason")}</Label><Input required value={correction.reason} onChange={(e) => setCorrection({ ...correction, reason: e.target.value })} data-testid="correction-reason" /></div>
              <Button onClick={saveCorrection} disabled={busy || !correction.qty || !correction.reason} data-testid="save-correction-button">{t("save")}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Panel>
    <OrderAdvice refreshKey={tick} />
    </div>
  );
}
